/**
 * The primary navigation: wordmark, organization switcher, grouped links.
 *
 * ## The wordmark switches scheme in CSS, not in JavaScript and not in `<picture>`
 *
 * `/brand/namu-logo.svg` is drawn in Ink for a light ground and
 * `namu-logo-light.svg` in Harmattan for a dark one. Both are rendered and CSS
 * chooses. The obvious alternative, `<picture>` with
 * `media="(prefers-color-scheme: dark)"`, is wrong here and quietly so: it
 * follows the operating system, and this product lets somebody pin light on a
 * dark machine. That person would get the cream wordmark, which is invisible
 * on cream, and the product would appear to have no logo at all. The rules in
 * the stylesheet reproduce the same tri-state `light-dark()` follows.
 *
 * ## Collapsed, the labels are clipped rather than removed
 *
 * `display: none` on the label would take the link's accessible name with it
 * and leave fourteen links called nothing. The label stays in the accessibility
 * tree and leaves the visual layout, which is what the `visually-hidden`
 * technique is for.
 */

import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { BlossomMark, ChevronUpDownIcon, PanelLeftIcon } from '@/components/icons/Icons'
import { DropdownMenu } from '@/components/overlays/DropdownMenu/DropdownMenu'
import type { MenuItem } from '@/components/overlays/DropdownMenu/DropdownMenu'
import { Button } from '@/components/primitives'
import { useShell } from '../shell'
import styles from './Sidebar.module.css'

export interface NavItem {
  to: string
  label: string
  icon: ReactNode
  /**
   * A count on the right of the row: open cases, tasks due. Rendered with its
   * meaning in the accessible name, because "12" read out on its own tells
   * somebody nothing about what twelve of.
   */
  badge?: number | string
  /** What the badge counts, for the accessible name. */
  badgeLabel?: string
  /** Matches only this exact path. Use on an index route. */
  end?: boolean
}

export interface NavGroup {
  /**
   * Optional, and the first group should usually not have one. A label above
   * the first two items in a list of five groups reads as a heading for all of
   * them.
   */
  label?: string
  items: readonly NavItem[]
}

export interface SidebarOrganization {
  id: string
  name: string
  /** The caller's role in this organization, shown beside the name. */
  role?: string
}

export interface SidebarProps {
  groups: readonly NavGroup[]
  organizations: readonly SidebarOrganization[]
  currentOrgId: string
  onSelectOrganization(id: string): void
  /** Sits at the bottom: support links, version, whatever the product needs. */
  footer?: ReactNode
  className?: string
}

export function Sidebar({
  groups,
  organizations,
  currentOrgId,
  onSelectOrganization,
  footer,
  className,
}: SidebarProps) {
  const { collapsed, setCollapsed, drawer, setDrawerOpen } = useShell()
  const current = organizations.find((organization) => organization.id === currentOrgId)

  const organizationItems: MenuItem[] = organizations.map((organization) => ({
    kind: 'radio',
    id: organization.id,
    label: organization.name,
    note: organization.role,
    checked: organization.id === currentOrgId,
    onSelect: () => onSelectOrganization(organization.id),
  }))

  const sidebarClassName = [styles.sidebar, className].filter(Boolean).join(' ')

  return (
    <div className={sidebarClassName} data-collapsed={collapsed} data-drawer={drawer}>
      <div className={styles.head}>
        <NavLink to="/" className={styles.brand} aria-label="Namu for Organizations, home">
          {collapsed ? (
            <BlossomMark size={22} className={styles.mark} />
          ) : (
            <>
              <img
                className={styles.wordmarkForLight}
                src="/brand/namu-logo.svg"
                alt=""
                width={360}
                height={130}
              />
              <img
                className={styles.wordmarkForDark}
                src="/brand/namu-logo-light.svg"
                alt=""
                width={360}
                height={130}
              />
            </>
          )}
        </NavLink>

        {/*
          Hidden in a drawer, where there is nothing to collapse to. The name is
          "Sidebar" in both states; whether it is open travels on aria-expanded,
          so the control can be found by name and queried for its state, which a
          button that renames itself "Expand sidebar" cannot be.
        */}
        {drawer ? null : (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            className={styles.collapse}
            aria-label="Sidebar"
            aria-expanded={!collapsed}
            onClick={() => setCollapsed(!collapsed)}
          >
            <PanelLeftIcon size={18} />
          </Button>
        )}
      </div>

      {/*
        One organization is a fact, not a choice. Rendering a switcher that
        opens onto a single item asks somebody to check every time whether they
        are in the right place, for a question that has one answer.
      */}
      <div className={styles.org}>
        {organizations.length > 1 ? (
          <DropdownMenu
            label="Organization"
            className={styles.orgMenu}
            triggerClassName={styles.orgTrigger}
            items={organizationItems}
            trigger={
              <>
                <span className={styles.orgName}>{current?.name ?? 'Choose an organization'}</span>
                <ChevronUpDownIcon size={16} className={styles.orgChevron} />
              </>
            }
          />
        ) : (
          <p className={styles.orgStatic}>{current?.name ?? ''}</p>
        )}
      </div>

      <nav className={styles.nav} aria-label="Primary">
        {groups.map((group, index) => (
          <div className={styles.group} key={group.label ?? `group-${index}`}>
            {group.label ? (
              <h2 className={styles.groupLabel}>
                {/* Clipped rather than removed when collapsed, so the groups
                    still exist for a screen reader at every width. */}
                <span className={collapsed ? 'visually-hidden' : undefined}>{group.label}</span>
              </h2>
            ) : null}

            <ul className={styles.list} role="list">
              {group.items.map((item) => (
                <li key={item.to}>
                  {/*
                    NavLink sets `aria-current="page"` on the active link by
                    itself, which is the state that matters here and the one
                    the accessibility floor names. The class is composed rather
                    than left to NavLink's own `active` class, because that one
                    is a plain string and CSS Modules would have hashed the
                    selector it is meant to match.
                  */}
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      [styles.link, isActive ? styles.active : ''].filter(Boolean).join(' ')
                    }
                    /* Choosing where to go is the drawer's entire job, so it
                       closes behind the choice rather than covering the page
                       that was just asked for. */
                    onClick={() => setDrawerOpen(false)}
                  >
                    <span className={styles.linkIcon} aria-hidden>
                      {item.icon}
                    </span>
                    <span className={collapsed ? 'visually-hidden' : styles.linkLabel}>
                      {item.label}
                    </span>
                    {item.badge === undefined ? null : (
                      <span className={styles.badge}>
                        <span aria-hidden>{item.badge}</span>
                        {/*
                          The comma and the leading space are load-bearing. An
                          accessible name is built by concatenating the text
                          nodes, and two adjacent spans with no whitespace
                          between them produce "Cases12 open cases".
                        */}
                        <span className="visually-hidden">
                          {`, ${item.badge}${item.badgeLabel ? ` ${item.badgeLabel}` : ''}`}
                        </span>
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {footer ? <div className={styles.foot}>{footer}</div> : null}
    </div>
  )
}
